import { h, Component } from 'preact';
import util from 'preact-util';
import { adminFormat } from '../../lib/raceTime';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

const REFRESH_INTERVAL_MS = 30 * 1000;

function getBatteryIcon(battery) {
    if (battery > 75) {
        return 'fa-battery-full text-success';
    }
    if (battery > 50) {
        return 'fa-battery-three-quarters text-success';
    }
    if (battery > 25) {
        return 'fa-battery-half text-warning';
    }
    if (battery > 10) {
        return 'fa-battery-quarter text-danger';
    }
    return 'fa-battery-empty text-danger';
}

function getOnlineClass(msSinceLast) {
    if (msSinceLast < 1000 * 60 * 10) {
        return 'text-success';
    }
    if (msSinceLast < 1000 * 60 * 30) {
        return 'text-warning';
    }
    if (msSinceLast < 1000 * 60 * 60) {
        return 'text-danger';
    }
    return 'text-body-secondary';
}

function formatAgo(timestamp, now) {
    if (!timestamp) {
        return '-';
    }
    const totalMinutes = Math.max(0, Math.round((now - new Date(timestamp).getTime()) / (60 * 1000)));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    let out = '';
    if (days > 0) out += `${days}d `;
    if (hours > 0 || days > 0) out += `${hours}h `;
    out += `${minutes}m`;
    return out;
}

@observer
class GpsDeviceStatusDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            error: null,
        };
    }

    componentDidMount() {
        this.loadStatus();
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
    }

    loadStatus = async () => {
        const { gpsDeviceId, stores } = this.props;
        const { gpsDeviceStore } = stores;
        if (!gpsDeviceId) {
            this.setState({ error: 'missing', isLoading: false });
            return;
        }
        this.setState({ isLoading: true });
        const data = await gpsDeviceStore.loadStatus(gpsDeviceId);
        this.setState({ isLoading: false, error: data ? null : 'load' });
        clearTimeout(this.timer);
        this.timer = setTimeout(this.loadStatus, REFRESH_INTERVAL_MS);
    }

    viewOnMap = () => {
        const { stores, drawerLevel = 1 } = this.props;
        const { appState, gpsDeviceStore } = stores;
        const { gpsDeviceStatus } = gpsDeviceStore;
        const { device = {}, status = {}, contestant } = gpsDeviceStatus;
        const { drawerHeightMedium } = appState;
        const title = contestant
            ? `#${contestant.bib || '-'} ${contestant.firstname || ''} ${contestant.lastname || ''}`
            : `GPS ${device.gpsId || device.gpsSerial || ''}`;
        appState.openDrawer('viewRaceMap', {
            raceId: device.race,
            raceClassId: device.raceClass,
            lat: status.lat,
            lng: status.lng,
            title,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    renderRecentPoints(recentPoints) {
        const { stores } = this.props;
        const race = stores?.raceStore?.race || {};
        if (!recentPoints || recentPoints.length === 0) {
            return null;
        }
        return (
            <div class='card mb-3'>
                <div class='card-body'>
                    <h6 class='card-title'>
                        <i class='fa-solid fa-list me-2' />
                        <Text id='gps-status.recent-points'>Last points</Text>
                    </h6>
                    <div class='table-responsive' style='font-size: 0.85em;'>
                        <table class='table table-sm table-striped mb-0'>
                            <thead>
                                <tr>
                                    <th><Text id='gps-status.time'>Time</Text></th>
                                    <th class='text-end'><Text id='gps-status.speed'>Speed</Text></th>
                                    <th class='text-end'><Text id='gps-status.battery'>Battery</Text></th>
                                    <th class='text-center'><Text id='gps-status.signal'>Signal</Text></th>
                                    <th><Text id='gps-status.position'>Position</Text></th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentPoints.map(pt => (
                                    <tr key={pt.timestamp}>
                                        <td><nobr>{adminFormat(pt.timestamp, race, 'isoDate')}</nobr></td>
                                        <td class='text-end'>{util.format(pt.speed, 1)}</td>
                                        <td class='text-end'>{pt.battery != null ? `${pt.battery}%` : '-'}</td>
                                        <td class='text-center'>{pt.type || '-'}</td>
                                        <td class='text-body-secondary'><nobr>{util.format(pt.lat, 5)}, {util.format(pt.lng, 5)}</nobr></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { isLoading, error } = this.state;
        const { stores } = this.props;
        const { gpsDeviceStore } = stores;
        const { gpsDeviceStatus = {} } = gpsDeviceStore;
        const { device, race: deviceRace, raceClass, contestant, status = {}, recentPoints = [] } = gpsDeviceStatus;
        const race = stores?.raceStore?.race || {};

        if (error === 'missing' || (error && !device)) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-danger'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        <Text id='gps-status.load-failed'>Could not load tracker status.</Text>
                    </div>
                </div>
            );
        }

        if (!device) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center' style='min-height: 200px;'>
                    <div class='spinner-border' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        const now = new Date().getTime();
        const hasPoints = status.count > 0;
        const msSinceLast = hasPoints && status.lastTimestamp ? now - new Date(status.lastTimestamp).getTime() : null;
        const onlineClass = msSinceLast != null ? getOnlineClass(msSinceLast) : 'text-body-secondary';
        const hasPosition = typeof status.lat === 'number' && typeof status.lng === 'number';

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 900px; padding: 20px; padding-bottom: 100px;'>

                    <div class='d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2'>
                        <h5 class='mb-0'>
                            <i class='fa-solid fa-satellite-dish me-2' />
                            <Text id='gps-status.title'>Tracker status</Text>
                            {contestant && <span class='ms-2'>#{contestant.bib || '-'} {contestant.firstname} {contestant.lastname}</span>}
                            {isLoading && <span class='spinner-border spinner-border-sm ms-2' />}
                        </h5>
                        <div class='d-flex align-items-center gap-2'>
                            {raceClass?.title && <span class='badge bg-secondary'>{raceClass.title}</span>}
                            <button type='button' class='btn btn-sm btn-outline-secondary' onClick={this.loadStatus} disabled={isLoading}>
                                <i class='fa-solid fa-rotate me-1' />
                                <Text id='gps-status.refresh'>Refresh</Text>
                            </button>
                        </div>
                    </div>

                    <div class='row g-2 mb-3'>
                        <div class='col-6 col-md-3'>
                            <div class='card h-100'>
                                <div class='card-body p-2 text-center'>
                                    <div class='small text-body-secondary'><Text id='gps-status.last-signal'>Last signal</Text></div>
                                    <div class={`fw-bold ${onlineClass}`}>
                                        <i class='fa-solid fa-circle-dot me-1' />
                                        {hasPoints ? formatAgo(status.lastTimestamp, now) : '-'}
                                    </div>
                                    {hasPoints && <div class='small text-body-secondary'><nobr>{adminFormat(status.lastTimestamp, race, 'isoDate')}</nobr></div>}
                                </div>
                            </div>
                        </div>
                        <div class='col-6 col-md-3'>
                            <div class='card h-100'>
                                <div class='card-body p-2 text-center'>
                                    <div class='small text-body-secondary'><Text id='gps-status.battery'>Battery</Text></div>
                                    <div class='fw-bold'>
                                        {hasPoints && status.battery != null ? (<>
                                            <i class={`fa-solid ${getBatteryIcon(status.battery)} me-1`} />
                                            {status.battery}%
                                        </>) : '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class='col-6 col-md-3'>
                            <div class='card h-100'>
                                <div class='card-body p-2 text-center'>
                                    <div class='small text-body-secondary'><Text id='gps-status.signal'>Signal</Text></div>
                                    <div class='fw-bold text-uppercase'>{hasPoints ? (status.type || '-') : '-'}</div>
                                    {hasPoints && <div class='small text-body-secondary'>sat {status.countSat || 0} / gsm {status.countGsm || 0}</div>}
                                </div>
                            </div>
                        </div>
                        <div class='col-6 col-md-3'>
                            <div class='card h-100'>
                                <div class='card-body p-2 text-center'>
                                    <div class='small text-body-secondary'><Text id='gps-status.speed'>Speed</Text></div>
                                    <div class='fw-bold'>{hasPoints && status.speed != null ? `${util.format(status.speed, 1)} km/h` : '-'}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {!hasPoints && (
                        <div class='alert alert-warning'>
                            <i class='fa-solid fa-signal-slash me-2' />
                            <Text id='gps-status.no-points'>No GPS points received from this tracker since it was activated.</Text>
                        </div>
                    )}

                    <div class='card mb-3'>
                        <div class='card-body'>
                            <h6 class='card-title'>
                                <i class='fa-solid fa-microchip me-2' />
                                <Text id='gps-status.device'>Tracker</Text>
                            </h6>
                            <div class='row small'>
                                <div class='col-6 col-md-3 mb-2'>
                                    <div class='text-body-secondary'><Text id='input.gpsNumber'>GPS Number</Text></div>
                                    <div class='fw-bold'>{device.gpsId || '-'}</div>
                                </div>
                                <div class='col-6 col-md-3 mb-2'>
                                    <div class='text-body-secondary'><Text id='gps-status.serial'>Serial</Text></div>
                                    <div class='fw-bold'>{device.gpsSerial || '-'}</div>
                                </div>
                                <div class='col-6 col-md-3 mb-2'>
                                    <div class='text-body-secondary'><Text id='gps-status.active-from'>Active from</Text></div>
                                    <div><nobr>{device.start ? adminFormat(device.start, race, 'isoDate') : '-'}</nobr></div>
                                </div>
                                <div class='col-6 col-md-3 mb-2'>
                                    <div class='text-body-secondary'><Text id='gps-status.active-to'>Active to</Text></div>
                                    <div><nobr>{device.end ? adminFormat(device.end, race, 'isoDate') : '-'}</nobr></div>
                                </div>
                                <div class='col-6 col-md-3 mb-2'>
                                    <div class='text-body-secondary'><Text id='gps-status.race'>Race</Text></div>
                                    <div>{deviceRace?.title || '-'}</div>
                                </div>
                                <div class='col-6 col-md-3 mb-2'>
                                    <div class='text-body-secondary'><Text id='gps-status.points-total'>Points total</Text></div>
                                    <div>{status.count || 0}</div>
                                </div>
                                <div class='col-6 col-md-3 mb-2'>
                                    <div class='text-body-secondary'><Text id='gps-status.first-point'>First point</Text></div>
                                    <div><nobr>{hasPoints && status.firstTimestamp ? adminFormat(status.firstTimestamp, race, 'isoDate') : '-'}</nobr></div>
                                </div>
                                <div class='col-6 col-md-3 mb-2'>
                                    <div class='text-body-secondary'><Text id='gps-status.position'>Position</Text></div>
                                    <div>
                                        {hasPosition ? (
                                            <button type='button' class='btn btn-link btn-sm p-0' onClick={this.viewOnMap}>
                                                <i class='fa-solid fa-map-location-dot me-1' />
                                                <Text id='gps-status.show-on-map'>Show on map</Text>
                                            </button>
                                        ) : '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {this.renderRecentPoints(recentPoints)}
                </div>
            </div>
        );
    }
}

export default GpsDeviceStatusDrawer;
